import MyLocationIcon from '@mui/icons-material/MyLocation';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Player from '../../../folders/subtitles/Player';
import { vibrantColors } from '../../../themes/VibrantTheme';

// The player panel is always dark, so its controls use the dark palette in both modes
const panelColors = vibrantColors.dark;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 4px;
    box-sizing: border-box;
    background: #0d0d0d;
`;

const PlayerWrapper = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const ControlBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    background: rgba(255, 255, 255, 0.04);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    flex-shrink: 0;
    flex-wrap: wrap;
`;

const SpeedLabel = styled.span`
    font-size: 11px;
    color: rgba(255, 255, 255, 0.6);
    white-space: nowrap;
`;

const BadgeRow = styled.div`
    display: flex;
    gap: 4px;
    padding: 2px 6px;
    flex-shrink: 0;
`;

const SPEEDS = [0.5, 1, 1.25, 1.5, 2];

/**
 * VideoPanel — wraps Player.js with:
 * - Playback speed chip row
 * - Follow Playhead toggle
 * - Large file / HLS badges
 * - HLS.js support for .m3u8 URLs
 *
 * NOTE: Player.js is rendered as-is; this component adds a wrapper + controls.
 * HLS is attached to the video element AFTER Player.js sets it via setPlayer.
 */
export default function VideoPanel({ followPlayhead, setFollowPlayhead, ...props }) {
    const { player, directUrl } = props;
    const [activeSpeed, setActiveSpeed] = useState(1);
    const [isLargeFile, setIsLargeFile] = useState(false);
    const hlsRef = useRef(null);
    const hlsAttachedRef = useRef(false);

    // Detect large file via Content-Length HEAD request
    useEffect(() => {
        if (!directUrl) return;
        const checkFileSize = async () => {
            try {
                const res = await fetch(directUrl, { method: 'HEAD' });
                const length = parseInt(res.headers.get('content-length') || '0', 10);
                if (length > 500 * 1024 * 1024) setIsLargeFile(true); // > 500MB
            } catch (_) {
                // Ignore — cross-origin HEAD may fail; badges are advisory only
            }
        };
        checkFileSize();
    }, [directUrl]);

    // HLS.js attachment — when player element is available and directUrl is HLS
    useEffect(() => {
        if (!player?.element || !directUrl) return;
        if (hlsAttachedRef.current) return;
        if (!directUrl.includes('.m3u8')) return;

        if (Hls.isSupported()) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }
            const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
            hls.loadSource(directUrl);
            hls.attachMedia(player.element);
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    console.warn('[HLS] Fatal error, falling back to native src:', data);
                    hls.destroy();
                    player.element.src = directUrl;
                }
            });
            hlsRef.current = hls;
            hlsAttachedRef.current = true;
        }
        // If browser supports native HLS (Safari), do nothing — Player.js handles native src
    }, [player, directUrl]);

    // Cleanup HLS on unmount
    useEffect(() => {
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, []);

    const handleSpeedChange = (speed) => {
        setActiveSpeed(speed);
        if (player?.element) {
            player.element.playbackRate = speed;
        }
    };

    return (
        <Wrapper>
            {/* Badge for large file */}
            {isLargeFile && (
                <BadgeRow>
                    <Chip
                        icon={<WarningAmberIcon />}
                        label="Large file — seeking may be slow"
                        size="small"
                        variant="outlined"
                        sx={{
                            fontSize: '0.7rem',
                            color: panelColors.warning,
                            borderColor: panelColors.warning,
                            '& .MuiChip-icon': { color: panelColors.warning },
                        }}
                    />
                </BadgeRow>
            )}

            {/* Video Player (unmodified Player.js) */}
            <PlayerWrapper>
                <Player {...props} hideSpeedControls />
            </PlayerWrapper>

            {/* Control Bar: speed chips + follow playhead toggle */}
            <ControlBar>
                <SpeedLabel>Speed:</SpeedLabel>
                {SPEEDS.map((speed) => (
                    <Chip
                        key={speed}
                        label={`${speed}x`}
                        size="small"
                        variant={activeSpeed === speed ? 'filled' : 'outlined'}
                        onClick={() => handleSpeedChange(speed)}
                        sx={{
                            cursor: 'pointer',
                            fontSize: '0.72rem',
                            height: 22,
                            fontWeight: activeSpeed === speed ? 700 : 500,
                            ...(activeSpeed === speed
                                ? {
                                      background: panelColors.primary,
                                      color: panelColors.onPrimary,
                                      '&:hover': { background: panelColors.primaryLight },
                                  }
                                : {
                                      color: 'rgba(255, 255, 255, 0.85)',
                                      borderColor: 'rgba(255, 255, 255, 0.3)',
                                      '&:hover': { background: 'rgba(255, 255, 255, 0.08)' },
                                  }),
                        }}
                    />
                ))}

                <Box sx={{ flexGrow: 1 }} />

                <Tooltip title={followPlayhead ? 'Following playhead — click to disable' : 'Follow playhead'} arrow>
                    <IconButton
                        size="small"
                        onClick={() => setFollowPlayhead((v) => !v)}
                        sx={{ color: followPlayhead ? panelColors.primaryLight : 'rgba(255, 255, 255, 0.55)' }}
                    >
                        <MyLocationIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </ControlBar>
        </Wrapper>
    );
}
